<template>
	<PageLoading
		:loading="market.initLoading || !market.collections.length"
		class="relative mt-4 pb-29.5"
	>
		<div class="flex gap-5 px-4">
			<RouterLink to="/inventory" custom v-slot="{ navigate }">
				<PageTitle
					@click="navigate"
					class="px-0! opacity-30"
					v-hover="{ active: 'scale-95' }"
				>
					{{ t('backpack') }}
				</PageTitle>
			</RouterLink>
			<PageTitle class="px-0!">{{ t('market') }}</PageTitle>
		</div>
		<PagePillRow class="mt-4 *:mx-0">
			<SearchButton v-model="market.search" />
			<BaseButton
				color="secondary"
				size="md"
				@click="market.toggleSort"
				class="snap-start"
			>
				<SortIcon
					class="h-5 w-5 transition-transform"
					:class="{ 'rotate-180': !market.sortExpensive }"
				/>
				<div>{{ sortText }}</div>
			</BaseButton>
			<BaseButton
				color="secondary"
				size="md"
				@click="openPriceFilter"
				class="snap-start"
			>
				<CoinsIcon class="h-5 w-5" />
				<div>{{ priceFilterText }}</div>
				<CaretRightIcon class="text-label-tertiary h-6 w-6 rotate-90" />
			</BaseButton>
			<BaseButton
				color="secondary"
				size="md"
				@click="openCollectionFilter"
				class="snap-start"
			>
				<CollectionIcon class="h-5 w-5" />
				<div>{{ collectionFilterText }}</div>
				<CaretRightIcon class="text-label-tertiary h-6 w-6 rotate-90" />
			</BaseButton>
		</PagePillRow>
		<PagePadding
			class="mt-4 grid grid-cols-2 gap-1.5 min-[480px]:grid-cols-3 min-[720px]:grid-cols-4 min-[960px]:grid-cols-5"
			:class="{ 'pointer-events-none opacity-50': market.loading }"
			v-if="market.items.length"
		>
			<MarketItemCard
				v-for="item in market.items"
				:src="icon(item.uuid)"
				:collection="item.collectionName"
				:name="item.name"
				:price="item.coins"
				:preloader="item.preloader || undefined"
				:selected="!!market.selected.find((i) => i.uuid === item.uuid)"
				@click="market.toggleSelection(item)"
				v-hover="{ active: 'scale-95' }"
			/>
			<div
				class="col-span-2 col-start-1 flex h-10 items-center justify-center"
				v-if="market.page !== market.pages"
			>
				<LoaderIcon class="text-separator-secondary h-5 w-5" />
			</div>
		</PagePadding>
		<PageEmpty
			:class="{ 'pointer-events-none opacity-50': market.loading }"
			:title="t('empty.title')"
			:desc="t('empty.desc')"
			v-else
		>
			<BaseButton size="lg" color="secondary" @click="reset">
				{{ t('reset') }}
			</BaseButton>
		</PageEmpty>
		<BottomActions class="border-b border-black">
			<div
				class="text-label-primary flex items-center justify-between gap-4 text-sm font-medium uppercase"
			>
				<div>{{ t('cart') }}</div>
				<div class="flex items-center gap-2.5">
					<div class="text-label-secondary">
						{{ t('items', market.selected.length) }}
					</div>
					<EmptyIcon
						class="text-fill-secondary h-6.5 w-6.5"
						v-if="!market.selected.length"
					/>
					<div
						class="flex items-center"
						v-else
						:class="{ 'pr-3.5': !moreItems }"
					>
						<div
							class="outline-fill-tertiary bg-fill-secondary relative -mr-3.5 h-6.5 w-6.5 rounded-full p-0.5 outline"
							v-for="item in slicedSelection"
						>
							<LazyImage
								:src="icon(item)"
								alt="selection"
								class="h-full w-full"
							/>
						</div>
						<div
							v-if="moreItems"
							class="bg-fill-secondary outline-fill-tertiary text-label-secondary relative inline-flex h-6.5 w-6.5 items-center justify-center rounded-full text-xs font-medium outline"
						>
							+{{ moreItems }}
						</div>
					</div>
				</div>
			</div>
			<BaseButton
				class="mt-4 w-full"
				@click="purchase"
				size="lg"
				color="primary"
				:disabled="purchaseModal || !market.selected.length"
			>
				<div>{{ t('purchase') }}</div>
				<CoinPrice :price="market.amount" />
			</BaseButton>
		</BottomActions>
		<PriceFilterModal
			v-model:min="market.minPrice"
			v-model:max="market.maxPrice"
			v-model:show="priceFilterModal"
		/>
		<CollectionFilterModal
			v-model:show="collectionFilterModal"
			v-model:selected="market.selectedCollections"
			:items="market.collections"
			@toggleSelection="market.toggleCollectionSelection"
		/>
		<CartModal
			v-model="purchaseModal"
			:items="market.selected"
			:amount="market.amount"
			@removeItem="market.removeSelection"
		/>
	</PageLoading>
</template>
<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import { useLayout } from '@/stores/layout'
import BaseButton from '@/components/BaseButton.vue'
import BottomActions from '@/components/BottomActions.vue'
import CoinPrice from '@/components/CoinPrice.vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import PagePillRow from '@/components/PagePillRow.vue'
import SortIcon from '@/assets/icons/sort.svg'
import PagePadding from '@/components/PagePadding.vue'
import SearchButton from '@/components/SearchButton.vue'
import { icon } from '@/utils/urls'
import PageLoading from '@/components/PageLoading.vue'
import EmptyIcon from '@/assets/icons/empty.svg'
import { useMarket } from '@/stores/market'
import CoinsIcon from '@/assets/icons/coins.svg'
import CaretRightIcon from '@/assets/icons/caret-right.svg'
import CollectionIcon from '@/assets/icons/collection.svg'
import PageEmpty from '@/components/PageEmpty.vue'
import PriceFilterModal from '@/components/modals/PriceFilterModal.vue'
import CollectionFilterModal from '@/components/modals/CollectionFilterModal.vue'
import MarketItemCard from '@/components/MarketItemCard.vue'
import LazyImage from '@/components/LazyImage.vue'
import CartModal from '@/components/modals/CartModal.vue'
import LoaderIcon from '@/assets/icons/loader.svg'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	messages: {
		en: {
			backpack: 'Backpack',
			market: 'Market',
			reset: 'reset filters',
			empty: {
				title: 'no gifts found',
				desc: 'try adjusting your filters'
			},
			purchase: 'purchase',
			sort: {
				expensive: 'Expensive first',
				cheap: 'Cheap first'
			},
			anyPrice: 'Any price',
			items: 'empty | 1 item | {n} items',
			collection: 'Collection',
			cart: 'cart'
		},
		ru: {
			backpack: 'Инвентарь',
			market: 'Маркет',
			reset: 'сбросить фильтры',
			empty: {
				title: 'гифтов не найдено',
				desc: 'Попробуй изменить фильтры'
			},
			purchase: 'купить',
			sort: {
				expensive: 'Сначала дорогие',
				cheap: 'Сначала дешевые'
			},
			anyPrice: 'Любая цена',
			items: 'пусто | {n} гифт | {n} гифта | {n} гифтов',
			collection: 'Коллекция',
			cart: 'корзина'
		}
	}
})

const layout = useLayout()

layout.showBottomNavbar()

const market = useMarket()

const sortText = computed(() => {
	return market.sortExpensive ? t('sort.expensive') : t('sort.cheap')
})

const purchaseModal = ref(false)
const purchase = () => {
	purchaseModal.value = true
}

const priceFilterModal = ref(false)
const openPriceFilter = () => {
	priceFilterModal.value = true
}

const collectionFilterModal = ref(false)
const openCollectionFilter = () => {
	collectionFilterModal.value = true
}

const reset = () => market.reset()

const priceFilterText = computed(() => {
	if (!market.minPrice && !market.maxPrice) {
		return t('anyPrice')
	}

	return `${market.minPrice || 0}${market.maxPrice ? ` - ${market.maxPrice}` : '+'}`
})

const slicedSelection = computed(() =>
	market.selected
		.slice(0)
		.sort((a, b) => b.coins - a.coins)
		.slice(0, 3)
		.map((item) => item.uuid)
)

const moreItems = computed(() =>
	market.selected.length > 3 ? market.selected.length - 3 : 0
)

const collectionFilterText = computed(() => {
	if (!market.selectedCollections.length) {
		return t('collection')
	}

	return `${market.selectedCollections[0]} ${market.selectedCollections.length > 1 ? `+ ${market.selectedCollections.length - 1}` : ''}`
})

const scroll = (e: Event) => {
	const target = e.target as HTMLDivElement

	const scrollTop = target.scrollTop
	const scrollHeight = target.scrollHeight
	const clientHeight = target.clientHeight

	if (scrollHeight - clientHeight - scrollTop < 420) {
		market.debouncedFetchMore()
	}
}

onMounted(() => {
	const view = document.querySelector('#view')

	if (!view) {
		return
	}

	view.addEventListener('scroll', scroll)
})

onBeforeUnmount(() => {
	const view = document.querySelector('#view')

	if (!view) {
		return
	}

	view.removeEventListener('scroll', scroll)
})
</script>
